import React, { useEffect, useRef, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import 'xterm/css/xterm.css';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../utils/axios';

const DeviceTerminal = ({ deviceSerial, accessMode = 'container', onClose }) => {
  const terminalRef = useRef(null);
  const terminal = useRef(null);
  const fitAddon = useRef(null);
  const eventSourceRef = useRef(null);
  const sessionIdRef = useRef(null);
  const isConnectedRef = useRef(false);
  const [sessionId, setSessionId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  
  // Update refs whenever state changes
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);
  
  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!deviceSerial) return;

    initializeTerminal();
    createTerminalSession();

    return () => {
      cleanup();
    };
  }, [deviceSerial]);

  // SSE connection for terminal output
  useEffect(() => {
    if (!sessionId) return;

    setupSSEConnection();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [sessionId]);

  const initializeTerminal = () => {
    // Initialize terminal
    terminal.current = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#d4d4d4',
        selection: '#264f78',
        black: '#000000',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#e5e510',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#666666',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#f5f543',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#e5e5e5'
      },
      convertEol: true,
      allowTransparency: true
    });

    fitAddon.current = new FitAddon();
    terminal.current.loadAddon(fitAddon.current);
    terminal.current.loadAddon(new WebLinksAddon());

    terminal.current.open(terminalRef.current);
    fitAddon.current.fit();

    // Handle input
    terminal.current.onData((data) => {
      const currentSessionId = sessionIdRef.current;
      const currentIsConnected = isConnectedRef.current;
      
      if (currentSessionId && currentIsConnected) {
        sendInput(data);
      }
    });

    // Handle resize
    terminal.current.onResize(({ cols, rows }) => {
      if (sessionId && isConnected) {
        resizeTerminal(cols, rows);
      }
    });

    // Handle window resize
    const handleResize = () => {
      if (fitAddon.current) {
        fitAddon.current.fit();
      }
    };
    window.addEventListener('resize', handleResize);

    // Store cleanup function
    terminal.current._cleanup = () => {
      window.removeEventListener('resize', handleResize);
    };

    // Welcome message
    terminal.current.write('\x1b[32mDM2500i Terminal\x1b[0m\r\n');
    terminal.current.write('\x1b[36mInitializing secure connection...\x1b[0m\r\n');
  };

  const setupSSEConnection = () => {
    try {
      const token = localStorage.getItem('token');
      const eventSource = new EventSource(`${import.meta.env.VITE_SSE_BASE_URL || 'http://localhost:3012/api/sse'}/events?token=${token}`);
      eventSourceRef.current = eventSource;
      
      eventSource.onopen = () => {
        console.log('SSE connection opened for terminal');
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Process SSE message
          
          if (data.type === 'terminal_ack' && data.sessionId === sessionId) {
            // Terminal acknowledgment received
            if (data.success && data.action === 'created') {
              // Terminal session confirmed by device
              setIsConnected(true);
              setError(null);
              if (terminal.current) {
                terminal.current.clear();
                terminal.current.write('\x1b[32mTerminal session established successfully!\x1b[0m\r\n');
                terminal.current.write(`\x1b[36mSecure connection to device: ${deviceSerial}\x1b[0m\r\n`);
                terminal.current.write('\x1b[33mSession timeout: 30 minutes of inactivity\x1b[0m\r\n');
                terminal.current.write('\r\n');
              }
            } else {
              // Terminal session creation failed
              setError(data.message || 'Failed to create terminal session');
              setIsConnected(false);
            }
          } else if (data.type === 'terminalOutput' && data.sessionId === sessionId) {
            // If we receive terminal output, the session is connected
            if (!isConnected) {
              // Terminal session is working (detected via output)
              setIsConnected(true);
              setError(null);
            }
            if (terminal.current) {
              terminal.current.write(data.data);
            }
          } else if (data.type === 'terminalExit' && data.sessionId === sessionId) {
            if (terminal.current) {
              terminal.current.write(`\r\n\x1b[31mSession ended (exit code: ${data.exitCode})\x1b[0m\r\n`);
            }
            setIsConnected(false);
          } else if (data.type === 'terminalError' && data.sessionId === sessionId) {
            if (terminal.current) {
              terminal.current.write(`\r\n\x1b[31mTerminal error: ${data.error}\x1b[0m\r\n`);
            }
            setError(data.error);
          }
        } catch (parseError) {
          console.error('Error parsing SSE message:', parseError);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE error:', error);
        setError('Connection lost. Attempting to reconnect...');
        setIsConnected(false);
        
        // Retry connection after 2 seconds
        setTimeout(() => {
          if (eventSourceRef.current?.readyState === EventSource.CLOSED) {
            console.log('Retrying SSE connection...');
            setupSSEConnection();
          }
        }, 2000);
      };
    } catch (error) {
      console.error('Failed to setup SSE connection:', error);
      setError('Failed to establish real-time connection');
    }
  };

  const createTerminalSession = async () => {
    if (!terminal.current) return;

    // Creating terminal session
    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post('/terminal/create', {
        deviceSerial,
        cols: terminal.current.cols || 80,
        rows: terminal.current.rows || 30,
        accessMode
      });

      // Terminal session API response received

      if (response.data.success) {
        const newSessionId = response.data.sessionId;
        // Setting sessionId
        setSessionId(newSessionId);
        
        // Wait for device acknowledgment via SSE
        
        if (terminal.current) {
          terminal.current.clear();
          terminal.current.write('\x1b[33mCreating terminal session...\x1b[0m\r\n');
          terminal.current.write('\x1b[36mAuthenticating with device...\x1b[0m\r\n');
          terminal.current.write('Please wait while we establish the connection...\r\n');
        }
        
        // Fallback timeout if no SSE response
        setTimeout(() => {
          if (!isConnected && newSessionId === sessionId) {
            setIsConnected(true);
            setError(null);
            if (terminal.current) {
              terminal.current.clear();
              terminal.current.write('\x1b[33mTerminal session established\x1b[0m\r\n');
              terminal.current.write(`\x1b[32mConnected to device: ${deviceSerial}\x1b[0m\r\n`);
              terminal.current.write('\x1b[36mYou can now type commands. Session will timeout after 30 minutes of inactivity.\x1b[0m\r\n');
              terminal.current.write('\r\n');
            }
          }
        }, 3000);
        
      } else {
        throw new Error(response.data.error || 'Failed to create terminal session');
      }
    } catch (error) {
      // Failed to create terminal session
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create terminal session';
      setError(errorMessage);
      
      if (terminal.current) {
        terminal.current.write(`\x1b[31mConnection Error: ${errorMessage}\x1b[0m\r\n`);
        terminal.current.write('\x1b[33mTroubleshooting:\x1b[0m\r\n');
        terminal.current.write('• Verify the device is online and connected\r\n');
        terminal.current.write('• Check your network connection\r\n');
        terminal.current.write('• Contact system administrator if problem persists\r\n');
        terminal.current.write('\r\n');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sendInput = async (data) => {
    const currentSessionId = sessionIdRef.current;
    const currentIsConnected = isConnectedRef.current;
    
    if (!currentSessionId || !currentIsConnected) {
      return;
    }

    try {
      await axiosInstance.post(`/terminal/${currentSessionId}/input`, {
        data
      });
    } catch (error) {
      console.error('Failed to send input:', error);
      if (error.response?.status === 404) {
        setError('Terminal session not found');
        setIsConnected(false);
      }
    }
  };

  const resizeTerminal = async (cols, rows) => {
    if (!sessionId || !isConnected) return;

    try {
      await axiosInstance.post(`/terminal/${sessionId}/resize`, {
        cols,
        rows
      });
    } catch (error) {
      console.error('Failed to resize terminal:', error);
    }
  };

  const destroyTerminalSession = async () => {
    if (!sessionId) return;
    
    try {
      await axiosInstance.delete(`/terminal/${sessionId}`);
    } catch (error) {
      console.error('Failed to destroy terminal session:', error);
    }
  };

  const cleanup = () => {
    if (sessionId) {
      destroyTerminalSession();
    }
    
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    if (terminal.current) {
      if (terminal.current._cleanup) {
        terminal.current._cleanup();
      }
      terminal.current.dispose();
      terminal.current = null;
    }
  };

  const handleReconnect = () => {
    setError(null);
    setIsConnected(false);
    setSessionId(null);
    
    if (terminal.current) {
      terminal.current.clear();
      terminal.current.write('Reconnecting...\r\n');
    }
    
    createTerminalSession();
  };

  return (
    <div className="terminal-container" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '600px', 
      width: '100%',
      backgroundColor: '#1e1e1e',
      border: '1px solid #333',
      borderRadius: '4px',
      overflow: 'hidden'
    }}>
      <div className="terminal-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 12px',
        backgroundColor: '#2d2d2d',
        borderBottom: '1px solid #333',
        color: '#d4d4d4',
        fontSize: '14px'
      }}>
        <div className="terminal-title">
          <span style={{ fontWeight: 'bold' }}>
            Host Terminal: {deviceSerial}
          </span>
          {isLoading && <span style={{ marginLeft: '8px', color: '#ffa500' }}>Connecting...</span>}
        </div>
        <div className="terminal-controls" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span className={`status ${isConnected ? 'connected' : 'disconnected'}`} style={{
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            backgroundColor: isConnected ? '#0dbc79' : '#cd3131',
            color: 'white'
          }}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          {error && !isConnected && (
            <button 
              onClick={handleReconnect}
              style={{
                padding: '4px 8px',
                backgroundColor: '#0dbc79',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Reconnect
            </button>
          )}
          <button 
            onClick={onClose} 
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#d4d4d4',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '4px 8px'
            }}
            title="Close terminal"
          >
            ×
          </button>
        </div>
      </div>
      
      {error && (
        <div style={{
          padding: '8px 12px',
          backgroundColor: '#cd3131',
          color: 'white',
          fontSize: '12px',
          borderBottom: '1px solid #333'
        }}>
          Error: {error}
        </div>
      )}
      
      <div 
        ref={terminalRef} 
        className="terminal-content"
        style={{ 
          flex: 1,
          padding: '8px',
          backgroundColor: '#1e1e1e'
        }}
      />
      
      <div className="terminal-footer" style={{
        padding: '4px 12px',
        backgroundColor: '#2d2d2d',
        borderTop: '1px solid #333',
        fontSize: '11px',
        color: '#888'
      }}>
        {sessionId && `Session: ${sessionId}`}
      </div>
    </div>
  );
};

export default DeviceTerminal;